import { createBrowserRouter } from "react-router-dom";
import { PageTransition, FeedbackModalProvider } from "@components/ui";
import {
  ResidentialHome,
  ResidentialServices,
  ResidentialNewConnections,
  ResidentialGasServices,
  ResidentialGasSolutions,
  ResidentialTariffs,
  ResidentialSafetyEmergency,
  ResidentialFAQ,
  ResidentialSafetyGuidelines,
  ResidentialCustomerFeedback,
  ResidentialBillingPayment,
  ResidentialWhyChooseGaz,
  ResidentialCareers,
  ResidentialJobDetails,
  ResidentialSustainability,
  ResidentialNewsUpdates,
  ResidentialContact,
  ResidentialPrivacyPolicy,
  ResidentialAboutUs,
} from "@features/residential";
import { BusinessLayout } from "@/layouts/BusinessLayout";
import {
  BusinessSolution,
  CommercialGasSolution,
  GasServices,
  NewConnection,
  PayBill,
  ServiceLevelAgreement,
  Tarrifs,
  RegulatoryCompliance,
  CorporateGovernance,
  DownloadsResourcesLibrary,
  BusinessWhyChooseGaz,
  BusinessSustainability,
  BusinessCareers,
  BusinessNewsUpdates,
  BusinessContact,
  BusinessSafetyEmergency,
  BusinessSafetyGuidelines,
  BusinessCustomerFeedback,
  BusinessFAQ,
  InvestorGovernance,
  InvestorRelations,
} from "@/features/business";
import { NotFound } from "@pages/NotFound";
import AboutUs from "@/features/business/pages/about";
import { PartnersLayout } from "@/layouts/PartnersLayout";
import {
  BusinessSolutions,
  DeveloperAndOwner,
  PartnersHome,
  SupplierAndVendor,
  PartnersWhyChooseGaz,
  PartnersSustainability,
  PartnersCareers,
  PartnersNewsUpdates,
  PartnersContact,
  PartnersAboutUs,
  PartnersCorporateGovernance,
  PartnersRegulatoryCompliance,
  PartnersDownloadResources,
  PartnersSafetyEmergency,
  PartnersSafetyGuidelines,
  PartnersCustomerFeedback,
  PartnersFAQ,
} from "@/features/partners";

/**
 * ============================================================================
 * Application Routes
 * ============================================================================
 *
 * Central routing configuration for the entire application.
 *
 * All routes are wrapped in a root PageTransition layout that provides:
 * - Fiery gradient progress bar on navigation
 * - Smooth fade-in + slide animation for page content
 * - Automatic scroll-to-top on route change
 *
 * Route Organization:
 * - /                    → Main landing page
 * - /services/*          → Residential service sub-pages
 * - /business/*          → Business customer pages
 * - *                    → 404 Not Found (catch-all)
 *
 * Each feature owns its own routes under its path prefix.
 * Unbuilt routes fall through to the NotFound page.
 * ============================================================================
 */

export const router = createBrowserRouter([
  {
    element: (
      <FeedbackModalProvider>
        <PageTransition />
      </FeedbackModalProvider>
    ),
    children: [
      // Landing page (has its own header/footer embedded)
      {
        path: "/",
        element: <ResidentialHome />,
      },
      // Residential page (same as landing)
      {
        path: "/residential",
        element: <ResidentialHome />,
      },

      // ========================================================================
      // RESIDENTIAL SERVICE SUB-PAGES (standalone — own header/footer)
      // ========================================================================
      {
        path: "/services/pay-bill",
        element: <ResidentialServices />,
      },
      {
        path: "/services/new-connections",
        element: <ResidentialNewConnections />,
      },
      {
        path: "/services/gas-services",
        element: <ResidentialGasServices />,
      },
      {
        path: "/services/gas-solutions",
        element: <ResidentialGasSolutions />,
      },
      {
        path: "/tariffs",
        element: <ResidentialTariffs />,
      },
      {
        path: "/safety",
        element: <ResidentialSafetyEmergency />,
      },
      {
        path: "/safety/guidelines",
        element: <ResidentialSafetyGuidelines />,
      },
      {
        path: "/faq",
        element: <ResidentialFAQ />,
      },
      {
        path: "/customer-feedback",
        element: <ResidentialCustomerFeedback />,
      },
      {
        path: "/services/billing-payment",
        element: <ResidentialBillingPayment />,
      },
      {
        path: "/about",
        element: <ResidentialAboutUs />,
      },
      {
        path: "/about/why-choose-gaz",
        element: <ResidentialWhyChooseGaz />,
      },
      {
        path: "/careers",
        element: <ResidentialCareers />,
      },
      {
        path: "/careers/:jobSlug",
        element: <ResidentialJobDetails />,
      },
      {
        path: "/sustainability",
        element: <ResidentialSustainability />,
      },
      {
        path: "/news",
        element: <ResidentialNewsUpdates />,
      },
      {
        path: "/contact",
        element: <ResidentialContact />,
      },
      {
        path: "/privacy-policy",
        element: <ResidentialPrivacyPolicy />,
      },
      {
        path: "/sla",
        element: <ServiceLevelAgreement />,
      },

      // ========================================================================
      // BUSINESS ROUTES
      // ========================================================================
      {
        path: "/business",
        children: [
          {
            element: <BusinessLayout />,
            children: [
              {
                path: "",
                element: <BusinessSolution />,
              },
              {
                path: "solutions/business-solutions-and-developer-services",
                element: <BusinessSolution />,
              },
              {
                path: "solutions/new-connection",
                element: <NewConnection />,
              },
              {
                path: "solutions/gas-services",
                element: <GasServices />,
              },
              {
                path: "solutions/commercial-fb-gas-solutions",
                element: <CommercialGasSolution />,
              },
              {
                path: "services/tariffs",
                element: <Tarrifs />,
              },
              {
                path: "services/online-service-pay-bill",
                element: <PayBill />,
              },
              {
                path: "services/service-level-agreement",
                element: <ServiceLevelAgreement />,
              },
              {
                path: "safety/safety-emergencies",
                element: <BusinessSafetyEmergency />,
              },
              {
                path: "safety/faqs",
                element: <BusinessFAQ />,
              },
              {
                path: "why-choose-gaz",
                element: <BusinessWhyChooseGaz />,
              },
              {
                path: "sustainability-corporate-responsibility",
                element: <BusinessSustainability />,
              },
              {
                path: "careers",
                element: <BusinessCareers />,
              },
              {
                path: "news-updates-announcements",
                element: <BusinessNewsUpdates />,
              },
              {
                path: "contact",
                element: <BusinessContact />,
              },
              {
                path: "about-us",
                element: <AboutUs />,
              },
              {
                path: "investor-governance-corporate",
                element: <InvestorGovernance />,
              },
              {
                path: "investor-relations",
                element: <InvestorRelations />,
              },
              {
                path: "safety/customer-feedback",
                element: <BusinessCustomerFeedback />,
              },
              {
                path: "safety/safety-guidelines",
                element: <BusinessSafetyGuidelines />,
              },
              {
                path: "resources/corporate-governance",
                element: <CorporateGovernance />,
              },
              {
                path: "resources/regulatory-compliance",
                element: <RegulatoryCompliance />,
              },
              {
                path: "resources/resources-library",
                element: <DownloadsResourcesLibrary />,
              },
            ],
          },
          // General pages accessible from business context
          {
            path: "privacy-policy",
            element: <ResidentialPrivacyPolicy />,
          },
          {
            path: "sla",
            element: <ServiceLevelAgreement />,
          },
          // Business 404 — catches /business/* that don't match above
          {
            path: "*",
            element: <NotFound />,
          },
        ],
      },

      // ========================================================================
      // PARTNERS ROUTES

      {
        path: "/partners",
        children: [
          {
            element: <PartnersLayout />,
            children: [
              {
                path: "",
                element: <PartnersHome />,
              },
              {
                path: "business-solutions-and-developer-services",
                element: <BusinessSolutions />,
              },
              {
                path: "developer-and-owner-association-solutions",
                element: <DeveloperAndOwner />,
              },
              {
                path: "supplier-and-vendor-portal",
                element: <SupplierAndVendor />,
              },
              // Safety & Support
              {
                path: "safety/safety-emergencies",
                element: <PartnersSafetyEmergency />,
              },
              {
                path: "safety/faqs",
                element: <PartnersFAQ />,
              },
              {
                path: "safety/safety-guidelines",
                element: <PartnersSafetyGuidelines />,
              },
              {
                path: "safety/customer-feedback",
                element: <PartnersCustomerFeedback />,
              },
              // Resources
              {
                path: "resources/corporate-governance",
                element: <PartnersCorporateGovernance />,
              },
              {
                path: "resources/regulatory-compliance",
                element: <PartnersRegulatoryCompliance />,
              },
              {
                path: "resources/resources-library",
                element: <PartnersDownloadResources />,
              },
              // Company pages
              {
                path: "about-us",
                element: <PartnersAboutUs />,
              },
              {
                path: "why-choose-gaz",
                element: <PartnersWhyChooseGaz />,
              },
              {
                path: "sustainability",
                element: <PartnersSustainability />,
              },
              {
                path: "careers",
                element: <PartnersCareers />,
              },
              {
                path: "news",
                element: <PartnersNewsUpdates />,
              },
              {
                path: "contact",
                element: <PartnersContact />,
              },
            ],
          },
          // Partners 404
          {
            path: "*",
            element: <NotFound />,
          },
        ],
      },

      // ========================================================================
      // CATCH-ALL 404 (standalone — own header/footer) — must stay last
      // ========================================================================
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);
